import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Plane, ArrowRight, CalendarClock, Users, Landmark, Stamp,
  Headphones, UserCircle2, LogOut, Loader2, TrainFront, Bus,
} from "lucide-react";
import SEO from "@/components/SEO";
import { useAuth } from "@/auth/AuthContext";

const ACCOUNT_TYPE_LABELS = { travel_partner: "Travel Partner", corporate_client: "Corporate Client" };
const ROLE_LABELS = {
  super_admin: "Super Admin", operations_admin: "Operations Admin",
  agency_admin: "Agency Admin", travel_consultant: "Travel Consultant", finance: "Finance",
  corporate_admin: "Corporate Admin", corporate_traveller: "Corporate Traveller", approver: "Approver",
};

const CHINA_SERVICES = [
  "China Visa Assistance", "China Hotels", "Airport Transfers", "High-Speed Rail",
  "Private Transportation", "Business Meetings", "Factory Visits",
  "Trade Fairs & Exhibitions", "Guides & Translators", "China Experiences",
];

const FutureCard = ({ icon: Icon, title, copy, testId }) => (
  <div className="bg-white border border-[#EAE6DF] p-7" data-testid={testId}>
    <div className="flex items-start justify-between">
      <Icon size={22} className="text-[#0E2A5C]" strokeWidth={1.5}/>
      <span className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">Future Ready</span>
    </div>
    <h3 className="font-heading text-lg text-[#0E2A5C] mt-4">{title}</h3>
    <p className="text-sm text-[#4A4A4A] mt-1.5 leading-relaxed">{copy}</p>
  </div>
);

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F5F0]"><Loader2 className="animate-spin text-[#C62828]" size={28}/></div>;
  }
  if (user === null) return <Navigate to="/login?next=/portal" replace />;

  const signOut = async () => { await logout(); navigate("/login"); };
  const lastLogin = user.last_login_at ? new Date(user.last_login_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="min-h-screen bg-[#F7F5F0] font-body text-[#121212]" data-testid="travel-portal">
      <SEO title="Travel Portal — The China Explorer" description="Your B2B travel account." path="/portal" />

      {/* Portal top bar */}
      <header className="bg-[#0E2A5C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="font-heading text-lg tracking-tight">The China Explorer</Link>
          <div className="flex items-center gap-3 md:gap-5">
            {user.org_name && (
              <span className="hidden sm:inline text-[11px] uppercase tracking-[0.15em] text-[#D4AF37] font-semibold" data-testid="portal-org-badge">
                {user.org_name} · {ACCOUNT_TYPE_LABELS[user.account_type] || ""}
              </span>
            )}
            <button onClick={signOut} data-testid="portal-signout"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] font-semibold border border-white/30 hover:border-[#D4AF37] hover:text-[#D4AF37] px-4 py-2 transition-colors">
              <LogOut size={12}/> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#C62828] font-semibold">✦ The China Explorer Travel Portal</p>
        <h1 className="font-heading text-4xl md:text-5xl mt-3 text-[#0E2A5C]" data-testid="portal-welcome">
          Welcome, {user.name.split(" ")[0]}.
        </h1>
        <p className="mt-3 text-[#4A4A4A]">China is our specialty. The world is within reach.</p>

        {/* PRIMARY: Global Flights */}
        <Link to="/flights" data-testid="portal-global-flights"
          className="mt-10 block bg-[#0E2A5C] text-white relative overflow-hidden group">
          <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_85%_20%,#D4AF37_0%,transparent_55%)] pointer-events-none"/>
          <div className="relative p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold flex items-center gap-2">
                <Plane size={14}/> Global Flights · Primary Product
              </p>
              <h2 className="font-heading text-3xl md:text-4xl mt-3">The World, One Search Away.</h2>
              <p className="mt-3 text-white/75 max-w-xl">Search worldwide flight options across full-service and low-cost carriers.</p>
            </div>
            <span className="inline-flex items-center gap-2.5 bg-[#C62828] group-hover:bg-[#B71C1C] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors whitespace-nowrap">
              Search Global Flights <ArrowRight size={14}/>
            </span>
          </div>
        </Link>

        {/* Secondary grid */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <FutureCard icon={CalendarClock} title="My Bookings" copy="Your flight and travel bookings will appear here once live booking is activated." testId="portal-my-bookings"/>
          <FutureCard icon={Users} title="Travellers" copy="Save traveller profiles for faster booking across your organization." testId="portal-travellers"/>
          <div className="bg-white border border-[#EAE6DF] p-7" data-testid="portal-visa">
            <Stamp size={22} className="text-[#C62828]" strokeWidth={1.5}/>
            <h3 className="font-heading text-lg text-[#0E2A5C] mt-4">China Visa Assistance</h3>
            <p className="text-sm text-[#4A4A4A] mt-1.5 leading-relaxed">Professional visa support for your travellers and delegations.</p>
            <Link to="/#contact" className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-[#0E2A5C] hover:text-[#C62828]">
              Request Assistance <ArrowRight size={12}/>
            </Link>
          </div>
        </div>

        {/* China Travel services */}
        <section className="mt-12 bg-white border border-[#EAE6DF] p-8 md:p-10" data-testid="portal-china-travel">
          <div className="flex items-center gap-3">
            <Landmark size={20} className="text-[#C62828]"/>
            <h2 className="font-heading text-2xl text-[#0E2A5C]">China Travel Services</h2>
          </div>
          <p className="mt-2 text-sm text-[#4A4A4A] max-w-2xl">
            Our core expertise — ground arrangements across China for your clients and teams. Request any service and our specialists respond directly.
          </p>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {CHINA_SERVICES.map((s) => (
              <span key={s} className="px-4 py-2 border border-[#EAE6DF] text-xs uppercase tracking-[0.15em] text-[#0E2A5C]">{s}</span>
            ))}
          </div>
          <Link to="/#contact" data-testid="portal-request-china-services"
            className="mt-7 inline-flex items-center gap-2.5 bg-[#0E2A5C] hover:bg-[#0A1F44] text-white px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
            Request China Services <ArrowRight size={13}/>
          </Link>
        </section>

        {/* Rail & Bus — secondary */}
        <div className="mt-6 grid sm:grid-cols-2 gap-6 max-w-3xl">
          {[{ icon: TrainFront, t: "Rail", c: "Seamless city-to-city journeys" }, { icon: Bus, t: "Bus", c: "Flexible ground connections" }].map((x) => (
            <div key={x.t} className="border border-[#EAE6DF] bg-white p-6 flex items-start gap-4">
              <x.icon size={20} className="text-[#0E2A5C]" strokeWidth={1.5}/>
              <div>
                <h3 className="font-heading text-base text-[#0E2A5C]">{x.t}</h3>
                <p className="text-xs text-[#4A4A4A] mt-0.5">{x.c}</p>
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mt-2">Launching soon</p>
              </div>
            </div>
          ))}
        </div>

        {/* Account + support */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <section className="bg-white border border-[#EAE6DF] p-8" data-testid="portal-my-account">
            <div className="flex items-center gap-3">
              <UserCircle2 size={20} className="text-[#0E2A5C]"/>
              <h2 className="font-heading text-xl text-[#0E2A5C]">My Account</h2>
            </div>
            <dl className="mt-5 grid gap-3 text-sm">
              {[
                ["Name", user.name],
                ["Email", user.email],
                ["Role", ROLE_LABELS[user.role] || user.role],
                ["Organization", user.org_name || "—"],
                ["Account Type", ACCOUNT_TYPE_LABELS[user.account_type] || "—"],
                ["Last Login", lastLogin],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-[#EAE6DF] pb-2 last:border-0">
                  <dt className="text-[#4A4A4A] text-xs uppercase tracking-[0.15em] font-semibold">{k}</dt>
                  <dd className="text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="bg-white border border-[#EAE6DF] p-8" data-testid="portal-support">
            <div className="flex items-center gap-3">
              <Headphones size={20} className="text-[#0E2A5C]"/>
              <h2 className="font-heading text-xl text-[#0E2A5C]">Support</h2>
            </div>
            <p className="mt-4 text-sm text-[#4A4A4A] leading-relaxed">
              Our travel specialists are available for bookings, quotations, visa questions and urgent requests.
            </p>
            <div className="mt-5 grid gap-2 text-sm">
              <a href="https://wa.me/971589656886" target="_blank" rel="noreferrer" className="text-[#0E2A5C] font-semibold hover:text-[#C62828]">WhatsApp: +971 58 965 6886</a>
              <a href="mailto:hello@thechinaexplorer.com" className="text-[#0E2A5C] font-semibold hover:text-[#C62828]">hello@thechinaexplorer.com</a>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-[#EAE6DF] py-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A4A4A]">The China Explorer · B2B Travel Platform</p>
      </footer>
    </div>
  );
}
